import { ReactNode } from "react";

export interface UserBankListReturnType {
  status_code: number;
  bank_list: BankList[];
}

export interface BankList {
  bank_name: string;
  code: string;
  img_url?: string;
}

export interface HostBankListReturnType {
  status_code: number;
  host_bank_list: HostBankList[];
}

export interface HostBankList {
  id: string;
  bank_name: string;
  bank_account_name: string;
  bank_account_no: string;
}

export interface PaymentGatewayProviderReturnType {
  status_code: number;
  data: PaymentGatewayProviderData;
}

export interface PaymentGatewayProviderData {
  payment_providers: PaymentProviders;
  payment_options: PaymentOptions;
  min_deposit: string;
  max_deposit: string;
}

export interface PaymentOptions {
  "atm-cdm": ATMCdm;
}

export interface ATMCdm {
  name: string;
  payment_provider: boolean;
  value: string;
}

export interface PaymentProviders {
  [provider: string]: PaymentProvider;
  // Rajapay: PaymentProvider;
}

export interface DepositOption {
  name: string;
  payment_provider: string[];
  value: string;
}

export interface DepositLimit {
  options: DepositOption[];
  min_deposit: string;
  max_deposit: string;
}

export interface PaymentProvider {
  type: string[];
  payout_supported_bank: PayoutSupportedBank;
  provider_details: ProviderDetails;
  deposit_options: DepositOptions;
  deposit: DepositLimit;
}

export interface ProviderDetails {
  endpoint: string;
  charges: number;
  form_name: string;
  short_name: string;
  type: string;
  remark: string;
  bonus: number;
}

export interface DepositOptions {
  BKash: BKash;
}

export interface BKash {
  name: string;
  payment_provider: string[];
  value: string;
}

export interface PayoutSupportedBank {}

export interface LoginReturnType {
  status_code: number;
  message: string;
  data: Data;
}

export interface Data {
  username: string;
  host_id: string;
  email: null;
  access_token: string;
  refresh_token: string;
  user_wallets: UserWallets;
  currency: string;
}

export interface UserWallets {
  primary: number;
  commission: number;
  rebate: number;
  promotion: number;
  in_game: number;
  actual_amount: number;
}

export interface RebateHistoryResponse {
  status_code: number;
  rebate_transaction: RebateTransaction[];
}

export interface RebateTransaction {
  date_time: string;
  trans_type: string;
  amount: number;
  remark: string;
}

export interface UserTransferRecordReturnType {
  status_code: number;
  page_count: number;
  records: TransferRecord[];
}

export enum RecordStatusType {
  APPROVED = "approved",
  PENDING = "pending",
  IN_PROGRESS = "in-progress",
  REJECTED = "rejected",
}

export interface TransferRecord {
  id: string;
  item_id: string;
  bank_name: null;
  account_no: null;
  tran_type: string;
  banking_option: string;
  amount: number;
  status: RecordStatusType;
  created_at: string;
}

export interface PromotionStatusReturnType {
  status_code: number;
  data: Data;
}

export interface Data {
  promotion: Promotion;
  turnover: Turnover;
}

export interface Promotion {
  promotion_id: string;
  promotion_name: string;
}

export interface Turnover {
  current: string;
  target: string;
  winover_current: string;
  winover_target: string;
  reset_date: Date;
}

export interface GetPromotionsReturnType {
  status_code: number;
  data: PromotionItem[];
}

export interface PromotionItem {
  id: string;
  name: string;
  description: string;
  start_date: string;
  end_date: string;
  new_player: number;
  deposit_option: string;
  bonus_option: string;
  percent: string;
  max_cap: number | string;
  amount: number | string;
  min_deposit: string;
  claim_limit: number;
  remaining_claim: number;
  application_times: string;
  rolling_multiplier: string;
  win_over_multiplier: string;
  count_from: Date;
  image: string;
  image2: string;
  expire_date: Date;
  position: number;
  promo_type?: string;
}

export interface Name {
  cn: string;
  en: string;
  in: string;
}

export interface UserBankDetailsReturnType {
  status_code: number;
  user_bank_detail: UserBankDetail[];
}

export interface UserBankDetail {
  uid: string;
  account_number: string;
  bank_name: string;
  bank_code: string;
  bank_image: string;
  account_type: string;
}

export enum BankMethod {
  GET = "get",
  CREATE = "post",
  UPDATE = "put",
  DELETE = "delete",
}

export interface UserDetailReturnType {
  status_code: number;
  user_information: UserInformation;
}

export interface UserInformation {
  username: string;
  email: null;
  phone_number: string;
  full_name: string;
  referral_code: string;
  date_of_birth: null;
  rank: Rank;
}

export interface Rank {
  label: string;
  description: string;
  min_exp: number;
  max_exp: number;
  reward_percent: string;
  min_retention_deposit: string;
  retention_duration: number;
  retention_penalty: string;
  image: null;
  extra_deposit_bonus_percentage: string;
  extra_exp_multiplier: number;
  rebate_percentage: string;
  upgrade_bonus: number;
  current_exp: number;
  renewal_date: string;
}

export interface GetAllGameListReturnType {
  status_code: number;
  game_list: GameListItem[];
  pagination: Pagination;
}

export interface Pagination {
  total_count: string;
  page_count: number;
  current_page: string;
  page_size: string;
}

export interface GameListItem {
  id: string;
  provider_id?: string;
  game_code?: string;
  ch?: string;
  eng?: string;
  group?: string;
  lobby_group?: string;
  game_type_id?: string;
  game_url?: string;
  img_url?: string;
  provider?: string;
  lobby_url?: string;
  sub_provider_name?: string;
  rtp: string;
}

export interface GameProviderLobbyViewReturnType {
  status_code: number;
  message: string;
  game_lobby_content: GameLobbyContent;
}

export interface GameLobbyContent {
  "": ProviderItem[];
}

export interface ProviderItem {
  provider_name: string;
  image_url?: string;
}

export interface RebateInfoReturnType {
  status_code: number;
  rebate_info: RebateInfo;
}

export interface RebateInfo {
  claimable_amount: number;
  turnover_multiplier: number;
  withdraw_turnover: number;
}

export interface ReportItem {
  game_provider_id?: string;
  game_provider?: string;
  game_code?: string;
  game_name_eng?: string;
  game_name_ch?: string;
  bet_amount?: string;
  win_amount?: string;
  loss_amount?: string;
  jackpot_amount?: string;
  profit_loss?: string;
  before_balance?: string;
  after_balance?: string;
  game_timestamp?: string;
}

export type ProcessedReportItem = ReportItem & { type: "date" | "transaction"; date?: string };

export interface PersonalReportReturnType {
  status_code: number;
  report: ReportItem[];
  page_count?: number;
}

export interface WalletBalanceReturnType {
  status_code: number;
  user_wallets: UserWallets;
  currency: string;
}

export interface UserWallets {
  primary: number;
  commission: number;
  rebate: number;
  promotion: number;
  "vip-bonus": number;
  "one-time-bonus": number;
  loyalty: number;
  in_game: number;
  actual_amount: number;
}

export enum GameType {
  GENERAL = 1,
  P2P = 2,
  SPORTS = 3, //
  LIVE = 4,
  ARCADE = 5,
  SLOT = 6,
  CRICKET = 7,
  FISHING = 8, // live: cricket
  CRASH = 9,
  TABLE_GAMES = 10,
  HOT_GAME = 1001, // Only appears in menu
  HOT_GAMES = 1002, // Appears in Dashboard and inside menu
}

export interface CategoryItem {
  icon: ReactNode;
  label: string;
  link: string;
  tag: string;
}

export interface UserRegisterReturnType {
  status_code: number;
  message: string;
  username: string;
  host_id: string;
  user_wallets: UserWallets;
  currency: string;
  access_token: string;
  refresh_token: string;
}

export interface UserWallets {
  primary: number;
  commission: number;
  rebate: number;
  promotion: number;
  "vip-bonus": number;
}

export interface CommissionSummaryReportReturnType {
  status_code: number;
  summary: Summary[];
}

export interface Summary {
  level: string;
  comm_amount: string;
}

export interface VipBonusInfoReturnType {
  status_code: number;
  data: VipBonusInfoData;
}

export interface VipBonusInfoData {
  claimable_amount: number;
  turnover_multiplier: number;
  withdraw_turnover: number;
}
export interface WithdrawTurnoverInfoReturnType {
  status_code: number;
  data: {
    current: string;
    target: string;
    winover_current: string;
    winover_target: string;
    reset_date: string | null;
  };
}

export interface GetAngpaoReturnType {
  status_code: number;
  event_list: AngpaoObject[];
  message: string;
}

export interface AngpaoObject {
  message: string;
  title: string;
  event_id: string;
  type: string;
  image_url: string;
  open_image_url: string;
}
export interface ClaimAngpaoReturnType {
  status_code: number;
  message: string;
  reward: number;
  type: string;
  image_url: string;
  open_image_url: string;
  title: string;
}

export interface MemberTreeReportReturnType {
  status_code: number;
  member_tree: MemberTree[];
}

export interface MemberTree {
  comm_amount: string;
  first_deposit: string;
  last_deposit: string;
  regiterd_date: string;
  upline_id: null | string;
  upline_username: string;
  user_id: string;
  username: string;
  vip_lable: string;
  vip_rank: string;
  downlines?: MemberTree[];
}

export type BankDetails = {
  method: BankMethod;
  data?:
    | {
        bank_code: string;
        account_number: string;
        account_type: "saving";
      }
    | {
        uid: number;
        bank_code: string;
        account_number: string;
        account_type: "saving";
      }
    | {
        uid: number;
      };
};

export interface IPGeolocationResponse {
  status: string;
  country: string;
  countryCode: string;
  region: string;
  regionName: string;
  city: string;
  zip: string;
  lat: number;
  lon: number;
  timezone: string;
  isp: string;
  org: string;
  as: string;
  query: string;
}

export interface WalletLog {
  id: string;
  user_id: string;
  wallet_id: string;
  game_provider_id: string;
  payment_gateway_provider_id: string;
  tran_type: string;
  source: string;
  transid: string;
  amount: number;
  before_balance: number;
  after_balance: number;
  description: string;
  remark: string;
  ip_address: string;
  user_agent: string;
  city: string;
  state: string;
  country: string;
  created_at: string;
  updated_at: string;
  deleted_at: string;
}

export type GameItem = {
  imgUrl?: string;
  provider?: string;
  gameType?: string;
  name?: string;
  id?: string;
  isHotGame?: boolean;
};

export enum EStatusCode {
  SUCCESS = 200,
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  FORBIDDEN = 403,
  NOT_FOUND = 404,
  INTERNAL_SERVER_ERROR = 500,
  SERVICE_UNAVAILABLE = 503,
}

export type AxiosCargo<T> = Promise<{ data: T; status: number; statusText: string }>;

export interface ProfileCardData {
  user: User;
  turnover: ITurnover;
  wallet: Wallet;
  lpPoint: number;
}

export interface ITurnover {
  current: number;
  target: number;
  progress: number;
}

export interface User {
  username: string;
  current_exp: number;
  phoneNumber: string;
}

export interface Wallet {
  currency: string;
  primary: number;
  commission: number;
  rebate: number;
  balance: number;
}

export enum UserDetailType {
  PHONE = "phone",
  USERNAME = "username",
}

export type Type = "REGISTER" | "DEPOSIT" | "WITHDRAWAL";

export enum NotificationStatus {
  READ,
  UNREAD,
}

export interface NotificationProps {
  id: string;
  username: string;
  type: "deposit" | "withdrawal";
  content: string;
  status: `${NotificationStatus}`;
  created_at: string;
  amount: string;
  payment_method: string;
  payment_provider?: string;
  tran_type: string;
}
